import { useLocation } from 'react-router-dom';
import { BottomNav } from './BottomNav';
import { GlassBackground } from './GlassBackground';
import { ActiveWorkoutBanner } from '../workout/ActiveWorkoutBanner';
import { ActiveWorkoutPill } from '../workout/ActiveWorkoutPill';
import { PageTransition } from './PageTransition';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useStreakReconcile } from '@/hooks/useStreakFreeze';
import { useTimezoneCapture } from '@/hooks/useTimezoneCapture';
import { useFinaleGate } from '@/hooks/useFinaleGate';
import { useIsImmersive } from '@/stores/immersive';


export function AppLayout() {
  const location = useLocation();

  // Auto-open the finale when a completed program is awaiting its reveal.
  useFinaleGate();


  // Initialize push notifications for native platforms
  usePushNotifications();

  // Initialize offline sync listener
  useOfflineSync();

  // Auto-reconcile streak on app open (consume freezes or break streak).
  useStreakReconcile();

  // Capture / heal user's IANA timezone (needed for retention notifier local-time math).
  useTimezoneCapture();

  // Hide bottom nav during the active workout (and HYROX setup) flow
  // so the user has full focus on the session.
  //
  // Also hidden for any registered "immersive" screen — intentionally-dark
  // full-bleed takeovers (share cards, cinematic loaders, first-run welcome).
  // Those are dark by design, and the nav + bottom scrim are theme-aware, so
  // in light mode they would otherwise paint light chrome over a dark screen.
  const immersive = useIsImmersive();

  const hideNav =
    immersive ||
    location.pathname.startsWith('/finale') ||
    location.pathname.includes('/workout/active') ||
    location.pathname.includes('/workout/hyrox-setup') ||
    
    location.pathname.includes('/workout/create-routine') ||
    location.pathname.includes('/workout/edit-routine') ||
    location.pathname.includes('/workout/summary') ||
    location.pathname.includes('/workout/hyrox-summary') ||
    location.pathname.startsWith('/body-analysis');


  return (
    <div className="h-[100dvh] flex flex-col bg-transparent overflow-hidden max-w-screen">
      <GlassBackground />
      <ActiveWorkoutBanner />
      <main
        id="main-scroll"
        className={`flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-safe ${
          hideNav ? '' : 'pb-safe-nav'
        }`}
        style={{ willChange: 'scroll-position', WebkitOverflowScrolling: 'touch' }}
      >
        <PageTransition />
      </main>
      {!hideNav && (
        <>
          {/* Bottom scrim — fades scrollable content before it reaches the nav */}
          {/* Anchored to the same safe-area inset the nav uses, plus the nav's own height (62px),
              so the fade meets the top edge of the nav with no gap on notch or home-button iPhones. */}
          <div
            aria-hidden
            className="fixed left-0 right-0 pointer-events-none"
            style={{
              bottom: `calc(max(env(safe-area-inset-bottom, 0px), 12px) + 62px)`,
              height: 64,
              background: 'linear-gradient(to top, var(--scrim-base) 0%, transparent 100%)',
              zIndex: 45,
            }}
          />
          <ActiveWorkoutPill />
          <BottomNav />
        </>
      )}
    </div>
  );
}
